<template>
  <section v-if="products.length" class="py-8 md:py-16">
    <UContainer>
      <div class="flex flex-col-reverse md:flex-row items-center gap-8 md:gap-12">
        <!-- Static Image Column (Left on desktop, Top on mobile) -->
        <div class="w-full md:w-7/12 order-1">
          <div class="w-full aspect-video md:aspect-636/720 relative rounded-3xl overflow-hidden">
            <img
              :src="featuredImage"
              :alt="featuredImageAlt"
              class="w-full h-full object-cover"
            />
          </div>
        </div>

        <!-- Products List Column (Right on desktop, Bottom on mobile) -->
        <div class="w-full md:w-5/12 order-2">
          <div class="md:ps-4 lg:ps-8">
            <!-- Header with Navigation -->
            <div class="flex items-center justify-between mb-6 md:mb-8">
              <h2 class="text-2xl md:text-3xl font-bold text-neutral">{{ $t('Popular products') }}</h2>
              <div class="flex gap-2">
                <UButton
                  icon="i-heroicons-chevron-left"
                  color="primary"
                  variant="outline"
                  size="lg"
                  square
                  @click="previousSlide"
                  :aria-label="$t('Previous')"
                />
                <UButton
                  icon="i-heroicons-chevron-right"
                  color="primary"
                  variant="outline"
                  size="lg"
                  square
                  @click="nextSlide"
                  :aria-label="$t('Next')"
                />
              </div>
            </div>

            <!-- Products Carousel -->
            <UCarousel
              ref="productsCarousel"
              :items="productGroups"
              :ui="{
                root: 'overflow-hidden',
                viewport: 'overflow-hidden',
                container: 'flex',
                item: 'flex-[0_0_100%] min-w-0',
                arrows: 'hidden'
              }"
              :arrows="false"
            >
              <template #default="{ item }">
                <div class="flex flex-col gap-3 lg:gap-4">
                  <NuxtLink
                    v-for="product in item"
                    :key="product.id"
                    :to="$localePath({ name: 'product-slug', params: { slug: product.slug } })"
                    class="flex items-center bg-gray-100 rounded-lg overflow-hidden transition-all hover:bg-gray-200 hover:shadow-md group"
                  >
                    <!-- Product Thumbnail -->
                    <div class="shrink-0 w-28 h-28">
                      <img
                        v-if="product.images.length"
                        :src="product.images[0]!.url"
                        :alt="product.name"
                        class="w-full h-full object-cover"
                      />
                      <img 
                        v-else
                        src="/img/placeholder.jpg"
                        :alt="product.name"
                        class="w-full h-full object-cover"
                      />
                    </div>
                    
                   <div class="flex flex-col gap-2 p-3 min-w-0 flex-1">
                      <h3 class="text-gray-900 font-medium truncate group-hover:underline">
                        {{ product.name }}
                      </h3>
                      <PriceCompare
                        :price="product.price"
                        :oldPrice="product.regularPrice"
                        class="text-gray-700"
                      />
                    </div>
                  </NuxtLink>
                </div>
              </template>
            </UCarousel>
          </div>
        </div>
      </div>
    </UContainer>
  </section>
</template>

<script setup lang="ts">
import type { ListProduct, Product } from '~/types'
import PriceCompare from '~/components/molecules/PriceCompare.vue'

const productsCarousel = useTemplateRef('productsCarousel')

const catalogService = useCatalogService()

const {
  data
} = await useAsyncData(() => catalogService.getProducts({
    sortBy: 'popularity',
    perPage: 9
}), { default: () => ({ data: [] }) })

const products = computed(() => data.value.data)

const nextSlide = () => {
  productsCarousel.value?.emblaApi?.scrollNext()
}

const previousSlide = () => {
  productsCarousel.value?.emblaApi?.scrollPrev()
}

// Static featured image
const featuredImage = 'https://picsum.photos/seed/featured-look/636/720'
const featuredImageAlt = 'Featured fashion look'

// Mock data - each array represents a slide with 3 products
const productGroups = ref<ListProduct[][]>(
  products.value.reduce((groups: ListProduct[][], product: ListProduct, index: number) => {
    const groupIndex = Math.floor(index / 3)
    if (!groups[groupIndex]) {
      groups[groupIndex] = []
    }
    groups[groupIndex].push(product)
    return groups
  }, [])
)
</script>
